import { Application, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { Graphic, Side } from '../sim/types';
import type { Projection } from '../map/projection';
import { symbolAsset } from './symbols';

/** One unit as the overlay needs it. The sim's `Unit` is reduced to this by the caller. */
export interface OverlayUnit {
  id: number;
  x: number; // AO centimeters - where the symbol is drawn (spec §7: the reported position when C2 is on)
  y: number;
  sidc: string;
  side: Side;
  strength: number; // 0..100
  suppression: number; // 0..100
  selected?: boolean;
  /** An order is in flight for this unit: draw the dashed pending ring. */
  pending?: boolean;
  label?: string;
  /**
   * The unit's true position, in AO centimeters, when it differs from where the symbol is drawn (the
   * reported position lagging it under C2). Only ever rendered - a faint dotted "report lag" line from
   * the symbol to this point - while the unit is `selected`, as a training aid (spec §7).
   */
  truePos?: { x: number; y: number };
}

/** One enemy contact as the overlay needs it. */
export interface OverlayContact {
  enemyId: number;
  x: number;
  y: number;
  /** Ring radius in meters: position error grown by age (see src/cop/spread.ts), not the raw report error. */
  errorM: number;
  ageMin: number;
  /** 1 on a fresh report, fading with age; a ghost uses the ghost alpha instead. */
  ringAlpha?: number;
  /** Screen-space role uncertainty, separate from the geographic position-error circle. */
  roleRingPx?: number;
  label: string;
  /** Older than 10 sim minutes (spec §7): rendered as a faded, hollow "last seen" ghost. */
  ghost?: boolean;
}

export interface OverlayCircle {
  x: number;
  y: number;
  rM: number;
}

const COLOR = {
  friendly: 0x58c4ff,
  hostile: 0xff5a5a,
  isr: 0x7ee081,
  warn: 0xffb347,
  paper: 0xe6ebf0,
  ink: 0x0b0f14,
} as const;

/** The reporting grid's line and label colour: the interface's "fog" slate, deliberately quieter than
 * any control graphic so the grid never competes with the plan drawn over it. */
const GRID_COLOR = 0x8a97a6;

const SYMBOL_SIZE = 22;
const HIT_RADIUS_PX = 18;
const MONO = 'IBM Plex Mono, ui-monospace, monospace';

interface UnitNode {
  root: Container;
  sprite: Sprite;
  deco: Graphics;
  label: Text;
  sidc: string;
  screenX: number;
  screenY: number;
}

interface ContactNode {
  root: Container;
  gfx: Graphics;
  text: Text;
}

/**
 * The PixiJS overlay that draws units, contacts and control graphics on top of a MapLibre map.
 *
 * Everything is stored in AO (area of operations) centimeters and reprojected to screen pixels
 * whenever the camera moves, so symbols stay glued to the terrain under pan, zoom and resize.
 * The canvas itself is `pointer-events: none`; clicks are read from the map container's DOM and
 * hit-tested against the last projected unit positions.
 */
export class Overlay {
  /** Resolves once the WebGL context exists and the first frame has been drawn. */
  readonly ready: Promise<void>;

  private app: Application | null = null;
  private destroyed = false;

  private readonly stage = new Container();
  /** The reporting grid (pitch 4), under everything: only drawn when `setGrid` turns it on. */
  private readonly gridLayer = new Graphics();
  private readonly coverageLayer = new Graphics();
  private readonly graphicsLayer = new Graphics();
  private readonly graphicsLabels = new Container();
  private readonly previewLayer = new Graphics();
  private readonly contactLayer = new Container();
  private readonly unitLayer = new Container();
  /** The selected unit's "report lag" training aid: a dotted line from its drawn (reported) position to
   * its true one, with a small label - only ever drawn for the selected unit, per `OverlayUnit.truePos`. */
  private readonly reportLagLine = new Graphics();
  private readonly reportLagLabel = new Text({
    text: 'report lag',
    style: new TextStyle({ fontFamily: MONO, fontSize: 9, fill: COLOR.warn }),
  });

  private units: OverlayUnit[] = [];
  private contacts: OverlayContact[] = [];
  private graphics: Graphic[] = [];
  private preview: Graphic | null = null;
  private coverage: OverlayCircle[] = [];
  private gridCellM: number | null = null;

  private unitNodes = new Map<number, UnitNode>();
  private contactNodes = new Map<number, ContactNode>();
  /**
   * Label pool. Text objects own a texture in the glyph atlas, so creating and destroying them
   * per frame thrashes the atlas and drops unrelated sprites out of the batch; labels are
   * therefore reused and merely hidden when a graphic goes away.
   */
  private labelPool: Text[] = [];
  private labelsUsed = 0;

  private unitClick: ((id: number) => void) | null = null;
  private mapClick: ((cm: { x: number; y: number }) => void) | null = null;

  private ro: ResizeObserver | null = null;
  private downAt: { x: number; y: number; t: number } | null = null;
  private dirty = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly map: MapLibreMap,
    private readonly proj: Projection,
  ) {
    this.reportLagLabel.anchor.set(0.5, 0.5);
    this.reportLagLabel.visible = false;
    this.stage.addChild(
      this.gridLayer,
      this.coverageLayer,
      this.graphicsLayer,
      this.graphicsLabels,
      this.previewLayer,
      this.contactLayer,
      this.unitLayer,
      this.reportLagLine,
      this.reportLagLabel,
    );
    this.ready = this.init();
  }

  private async init(): Promise<void> {
    const app = new Application();
    const { width, height } = this.size();
    await app.init({
      width,
      height,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      autoStart: false,
      preference: 'webgl',
    });
    if (this.destroyed) {
      app.destroy(true, { children: true });
      return;
    }
    this.app = app;
    // MapLibre drives the frame: rendering Pixi from the map's own render event keeps the
    // overlay exactly one transform behind zero frames, so symbols never lag the basemap.
    app.stop();
    app.stage.addChild(this.stage);

    const canvas = app.canvas as HTMLCanvasElement;
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '5';
    canvas.setAttribute('data-overlay', 'units');
    this.container.appendChild(canvas);

    this.map.on('move', this.markDirty);
    this.map.on('render', this.syncIfDirty);
    this.map.on('resize', this.handleResize);
    this.ro = new ResizeObserver(this.handleResize);
    this.ro.observe(this.container);

    this.container.addEventListener('pointerdown', this.handlePointerDown);
    this.container.addEventListener('click', this.handleClick);

    this.rebuildUnits();
    this.rebuildContacts();
    this.sync();
  }

  // ---------------------------------------------------------------- data in

  setUnits(units: OverlayUnit[]): void {
    this.units = units;
    this.rebuildUnits();
    this.sync();
  }

  setContacts(contacts: OverlayContact[]): void {
    this.contacts = contacts;
    this.rebuildContacts();
    this.sync();
  }

  setGraphics(graphics: Graphic[]): void {
    this.graphics = graphics;
    this.sync();
  }

  setPreview(graphic: Graphic | null): void {
    this.preview = graphic;
    this.sync();
  }

  setUasCoverage(circles: OverlayCircle[]): void {
    this.coverage = circles;
    this.sync();
  }

  /**
   * The reporting grid, on or off (pitch 4). `cellM` is the spacing in AO meters; `null` turns it off.
   *
   * A four-digit grid reference (`src/ui/reports.ts` `gridRef`) divides the AO into 100 x 100 squares
   * whatever its cell resolution, so a caller that wants "grid 5071" to land somewhere a finger can be
   * put should pass a whole multiple of `widthM / 100` — then every line drawn here is a real grid
   * boundary and its two-digit label is the reference's own digits.
   */
  setGrid(cellM: number | null): void {
    this.gridCellM = cellM && cellM > 0 ? cellM : null;
    this.sync();
  }

  onClickUnit(cb: (id: number) => void): void {
    this.unitClick = cb;
  }

  onClickMap(cb: (cm: { x: number; y: number }) => void): void {
    this.mapClick = cb;
  }

  /** Last projected screen position of a unit, in container CSS pixels (for tests and tooltips). */
  screenPosOf(id: number): { x: number; y: number } | null {
    const n = this.unitNodes.get(id);
    return n ? { x: n.screenX, y: n.screenY } : null;
  }

  destroy(): void {
    this.destroyed = true;
    this.map.off('move', this.markDirty);
    this.map.off('render', this.syncIfDirty);
    this.map.off('resize', this.handleResize);
    this.ro?.disconnect();
    this.ro = null;
    this.container.removeEventListener('pointerdown', this.handlePointerDown);
    this.container.removeEventListener('click', this.handleClick);
    const app = this.app;
    this.app = null;
    if (app) {
      const canvas = app.canvas as HTMLCanvasElement;
      app.destroy(true, { children: true });
      canvas.remove();
    }
    this.unitNodes.clear();
    this.contactNodes.clear();
  }

  // ------------------------------------------------------------- projection

  private size(): { width: number; height: number } {
    const r = this.container.getBoundingClientRect();
    return { width: Math.max(1, Math.round(r.width)), height: Math.max(1, Math.round(r.height)) };
  }

  private project(xCm: number, yCm: number): { x: number; y: number } {
    const [lng, lat] = this.proj.cmToLngLat(xCm, yCm);
    const p = this.map.project([lng, lat]);
    return { x: p.x, y: p.y };
  }

  /** Length in screen pixels of a horizontal distance in meters at a given AO position. */
  private metersToPx(meters: number, atX: number, atY: number): number {
    const a = this.project(atX, atY);
    const b = this.project(atX + meters * 100, atY);
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  private handleResize = (): void => {
    if (!this.app) return;
    const { width, height } = this.size();
    this.app.renderer.resize(width, height);
    this.sync();
  };

  /**
   * The camera moved. MapLibre always repaints after a camera change, so the actual reprojection
   * is deferred to the map's own `render` event: that runs after the map transform is committed
   * and after the basemap is drawn, which is what keeps the symbols from trailing by a frame.
   */
  private markDirty = (): void => {
    this.dirty = true;
  };

  private syncIfDirty = (): void => {
    if (this.dirty) this.sync();
  };

  /** Reproject everything and draw one frame. */
  private sync = (): void => {
    const app = this.app;
    this.dirty = false;
    if (!app || this.destroyed) return;
    this.layoutUnits();
    this.layoutContacts();
    this.drawGraphics();
    // Between the two: `drawGraphics` resets the shared label pool and `drawPreview` hides whatever
    // tail of it went unused, so the grid's edge labels have to be claimed in that window.
    this.drawGrid();
    this.drawPreview();
    this.drawCoverage();
    this.drawReportLag();
    app.render();
  };

  // ------------------------------------------------------------------ units

  private rebuildUnits(): void {
    const seen = new Set<number>();
    for (const u of this.units) {
      seen.add(u.id);
      let node = this.unitNodes.get(u.id);
      if (!node || node.sidc !== u.sidc) {
        if (node) {
          this.unitLayer.removeChild(node.root);
          node.root.destroy({ children: true });
        }
        node = this.makeUnitNode(u);
        this.unitNodes.set(u.id, node);
        this.unitLayer.addChild(node.root);
      }
      this.styleUnitNode(node, u);
    }
    for (const [id, node] of this.unitNodes) {
      if (seen.has(id)) continue;
      this.unitLayer.removeChild(node.root);
      node.root.destroy({ children: true });
      this.unitNodes.delete(id);
    }
  }

  private makeUnitNode(u: OverlayUnit): UnitNode {
    const root = new Container();
    const asset = symbolAsset(u.sidc, SYMBOL_SIZE);
    const sprite = new Sprite(asset.texture);
    sprite.anchor.set(asset.anchor.x, asset.anchor.y);
    const deco = new Graphics();
    // Always built, so a unit that gains a callsign later does not need a new node.
    const label = new Text({
      text: u.label ?? '',
      style: new TextStyle({ fontFamily: MONO, fontSize: 10, fill: COLOR.paper }),
    });
    label.anchor.set(0.5, 0);
    label.y = 27;
    root.addChild(deco, sprite, label);
    return { root, sprite, deco, label, sidc: u.sidc, screenX: 0, screenY: 0 };
  }

  /** Screen-space decoration: strength bar, suppression halo, selection and pending rings. */
  private styleUnitNode(node: UnitNode, u: OverlayUnit): void {
    const side = u.side === 0 ? COLOR.friendly : COLOR.hostile;
    const g = node.deco;
    g.clear();

    const supp = Math.max(0, Math.min(100, u.suppression));
    if (supp > 5) {
      g.circle(0, 0, 16).fill({ color: COLOR.warn, alpha: 0.05 + (supp / 100) * 0.3 });
    }
    if (u.selected) {
      g.circle(0, 0, 17).stroke({ width: 2, color: COLOR.friendly, alpha: 0.95 });
    }
    if (u.pending) {
      dashedCircle(g, 0, 0, 21, 5, 4);
      g.stroke({ width: 1.5, color: COLOR.warn, alpha: 0.9 });
    }

    // Strength bar under the symbol.
    const barW = 22;
    const barY = 20;
    const frac = Math.max(0, Math.min(1, u.strength / 100));
    g.rect(-barW / 2 - 1, barY - 1, barW + 2, 5).fill({ color: COLOR.ink, alpha: 0.75 });
    g.rect(-barW / 2, barY, barW, 3).fill({ color: 0x26313d, alpha: 0.9 });
    if (frac > 0) g.rect(-barW / 2, barY, barW * frac, 3).fill({ color: side, alpha: 1 });

    // Suppression drains the colour out of the symbol; the amber halo above is what says "warning".
    // Tinting the symbol amber instead (which is what this did) multiplies amber over friendly cyan
    // and lands on olive green — a colour this interface has already spent on the ISR family, so a
    // suppressed friendly platoon read as a different kind of unit rather than as a platoon in trouble.
    node.sprite.tint = mixTint(0xffffff, 0x8a97a6, supp / 160);
    node.sprite.alpha = u.strength <= 0 ? 0.35 : 1;
    const text = u.label ?? '';
    if (node.label.text !== text) node.label.text = text;
    node.label.visible = text !== '';
  }

  private layoutUnits(): void {
    for (const u of this.units) {
      const node = this.unitNodes.get(u.id);
      if (!node) continue;
      const p = this.project(u.x, u.y);
      node.screenX = p.x;
      node.screenY = p.y;
      node.root.position.set(p.x, p.y);
      node.root.visible = p.x > -80 && p.y > -80 && p.x < this.app!.renderer.width + 80 && p.y < this.app!.renderer.height + 80;
    }
  }

  // --------------------------------------------------------------- contacts

  private rebuildContacts(): void {
    const seen = new Set<number>();
    for (const c of this.contacts) {
      seen.add(c.enemyId);
      let node = this.contactNodes.get(c.enemyId);
      if (!node) {
        const root = new Container();
        const gfx = new Graphics();
        const text = new Text({
          text: '',
          style: new TextStyle({ fontFamily: MONO, fontSize: 10, fill: COLOR.hostile }),
        });
        text.anchor.set(0.5, 0);
        text.y = 12;
        root.addChild(gfx, text);
        this.contactLayer.addChild(root);
        node = { root, gfx, text };
        this.contactNodes.set(c.enemyId, node);
      }
      // A ghost's label is already the full "last seen H+MM:SS" string the caller built; a live
      // contact's label is its short type mark, so the age still needs appending here.
      node.text.text = c.ghost ? c.label : `${c.label} ${Math.max(0, Math.round(c.ageMin))}m`;
    }
    for (const [id, node] of this.contactNodes) {
      if (seen.has(id)) continue;
      this.contactLayer.removeChild(node.root);
      node.root.destroy({ children: true });
      this.contactNodes.delete(id);
    }
  }

  private layoutContacts(): void {
    for (const c of this.contacts) {
      const node = this.contactNodes.get(c.enemyId);
      if (!node) continue;
      const p = this.project(c.x, c.y);
      node.root.position.set(p.x, p.y);
      const rPx = Math.max(4, this.metersToPx(c.errorM, c.x, c.y));
      const g = node.gfx;
      g.clear();
      // A stale "last seen" ghost (spec §7: older than 10 sim minutes) fades the whole marker rather
      // than redrawing it differently - it is the same hollow diamond, just quieter.
      const alpha = c.ghost ? 0.35 : c.ringAlpha ?? 1;
      // Position uncertainty: the bigger the ring, the fuzzier the report. The fill is faded in
      // proportion to the radius rather than held at a flat 10%, because a dozen hour-old contacts
      // dead-reckoned out to their 3 km cap are a dozen overlapping discs, and at a flat alpha they
      // stack into one solid red wash across the north half of the map with the objective under it.
      // Each ring now lays down about the same amount of ink whatever its size.
      const fillAlpha = 0.1 * alpha * Math.min(1, 70 / rPx);
      g.circle(0, 0, rPx).fill({ color: COLOR.hostile, alpha: fillAlpha });
      g.circle(0, 0, rPx).stroke({ width: 1, color: COLOR.hostile, alpha: 0.45 * alpha });
      if (c.roleRingPx !== undefined && !c.ghost) {
        // Green broken ring = assessed role. Red geographic circle = reported position uncertainty.
        for (let segment = 0; segment < 4; segment++) {
          const start = segment * Math.PI / 2;
          g.moveTo(Math.cos(start) * c.roleRingPx, Math.sin(start) * c.roleRingPx)
            .arc(0, 0, c.roleRingPx, start, start + Math.PI / 3)
            .stroke({ width: 1.5, color: COLOR.isr, alpha: 0.8 * alpha });
        }
      }
      // Contact marker: hostile diamond outline (never filled, live or ghost).
      const d = 7;
      g.poly([0, -d, d, 0, 0, d, -d, 0], true).stroke({ width: 1.75, color: COLOR.hostile, alpha: 0.95 * alpha });
      node.text.alpha = alpha;
      node.text.y = Math.max(12, rPx + 3);
    }
  }

  // --------------------------------------------------------------- graphics

  private drawCoverage(): void {
    const g = this.coverageLayer;
    g.clear();
    for (const c of this.coverage) {
      const p = this.project(c.x, c.y);
      const r = this.metersToPx(c.rM, c.x, c.y);
      g.circle(p.x, p.y, r).fill({ color: COLOR.isr, alpha: 0.07 });
      g.circle(p.x, p.y, r).stroke({ width: 1, color: COLOR.isr, alpha: 0.25 });
    }
  }

  /**
   * The selected unit's report-lag trail (spec §7): only drawn when a unit is both selected and carries
   * a `truePos` distinct from where its symbol sits - i.e. C2 (command and control) is on and its report
   * is actually lagging. At most one unit is ever selected, so a single reused line and label are enough.
   */
  private drawReportLag(): void {
    const g = this.reportLagLine;
    g.clear();
    const u = this.units.find((x) => x.selected && x.truePos);
    if (!u || !u.truePos || (Math.abs(u.truePos.x - u.x) < 1 && Math.abs(u.truePos.y - u.y) < 1)) {
      this.reportLagLabel.visible = false;
      return;
    }
    const a = this.project(u.x, u.y);
    const b = this.project(u.truePos.x, u.truePos.y);
    dashedPolyline(g, [a, b], 2, 5);
    g.stroke({ width: 1, color: COLOR.warn, alpha: 0.6 });
    this.reportLagLabel.text = 'report lag';
    this.reportLagLabel.position.set((a.x + b.x) / 2, (a.y + b.y) / 2 - 8);
    this.reportLagLabel.visible = true;
  }

  /**
   * The reporting grid: one faint line per grid boundary across the whole AO, each labelled with the
   * two digits it contributes to a four-digit grid reference. Off unless `setGrid` turns it on.
   *
   * Labels sit at the north and west edges of the AO and are clamped into the container, so they stay
   * readable when the camera is panned off the corner rather than sliding out of sight with the line.
   */
  private drawGrid(): void {
    const g = this.gridLayer;
    g.clear();
    const cellM = this.gridCellM;
    if (cellM === null) return;

    const wCm = this.proj.widthM * 100;
    const hCm = this.proj.heightM * 100;
    const stepCm = cellM * 100;
    // The reference's own square, so a label can be printed as the reference's digits.
    const refCm = wCm / 100;
    const { width, height } = this.size();
    const clampX = (x: number): number => Math.max(14, Math.min(width - 14, x));
    const clampY = (y: number): number => Math.max(14, Math.min(height - 14, y));

    const north = this.project(0, hCm).y;
    const west = this.project(0, 0).x;

    for (let x = 0; x <= wCm + 1; x += stepCm) {
      const cm = Math.min(x, wCm);
      const a = this.project(cm, 0);
      const b = this.project(cm, hCm);
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
    }
    for (let y = 0; y <= hCm + 1; y += stepCm) {
      const cm = Math.min(y, hCm);
      const a = this.project(0, cm);
      const b = this.project(wCm, cm);
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
    }
    g.stroke({ width: 1, color: GRID_COLOR, alpha: 0.28 });

    const two = (n: number): string => String(Math.min(99, Math.round(n))).padStart(2, '0');
    for (let x = 0; x < wCm; x += stepCm) {
      const p = this.project(x, hCm);
      this.label(two(x / refCm), clampX(p.x + 12), clampY(north + 10), GRID_COLOR, 0.85);
    }
    for (let y = 0; y < hCm; y += stepCm) {
      const p = this.project(0, y);
      this.label(two(y / refCm), clampX(west + 12), clampY(p.y - 10), GRID_COLOR, 0.85);
    }
  }

  /** Graphics and preview share the label pool, so both are redrawn in one pass. */
  private drawGraphics(): void {
    const g = this.graphicsLayer;
    g.clear();
    this.labelsUsed = 0;
    for (const item of this.graphics) this.drawGraphic(g, item, 1);
  }

  private drawPreview(): void {
    const g = this.previewLayer;
    g.clear();
    if (this.preview) this.drawGraphic(g, this.preview, 0.5, false);
    for (let i = this.labelsUsed; i < this.labelPool.length; i++) this.labelPool[i].visible = false;
  }

  private label(text: string, x: number, y: number, color: number, alpha: number): void {
    let t = this.labelPool[this.labelsUsed];
    if (!t) {
      t = new Text({
        text,
        style: new TextStyle({ fontFamily: MONO, fontSize: 11, fontWeight: '500', fill: color }),
      });
      t.anchor.set(0.5, 0.5);
      this.labelPool.push(t);
      this.graphicsLabels.addChild(t);
    }
    this.labelsUsed++;
    if (t.text !== text) t.text = text;
    if (t.style.fill !== color) t.style.fill = color;
    t.visible = true;
    t.alpha = alpha;
    t.position.set(x, y);
  }

  private drawGraphic(g: Graphics, item: Graphic, alpha: number, withLabels = true): void {
    const pts = item.pts.map((p) => this.project(p.x, p.y));
    if (pts.length === 0) return;
    const flat: number[] = [];
    for (const p of pts) flat.push(p.x, p.y);

    switch (item.kind) {
      case 'axis': {
        // Axis of advance: a cyan polyline ending in an arrowhead.
        polyline(g, pts);
        g.stroke({ width: 2.5, color: COLOR.friendly, alpha });
        const a = pts[pts.length - 2] ?? pts[0];
        const b = pts[pts.length - 1];
        arrowHead(g, a, b, 13);
        g.fill({ color: COLOR.friendly, alpha });
        if (withLabels) this.label(item.name, b.x, b.y - 16, COLOR.friendly, alpha);
        break;
      }
      case 'obj': {
        g.poly(flat, true).fill({ color: COLOR.friendly, alpha: alpha * 0.08 });
        g.poly(flat, true).stroke({ width: 2, color: COLOR.friendly, alpha });
        if (withLabels) {
          const c = centroid(pts);
          this.label(item.name, c.x, c.y, COLOR.friendly, alpha);
        }
        break;
      }
      case 'pl': {
        polyline(g, pts);
        g.stroke({ width: 2, color: COLOR.friendly, alpha });
        if (withLabels) {
          // Phase lines are labelled at both ends, as on a paper overlay.
          this.label(item.name, pts[0].x, pts[0].y - 10, COLOR.friendly, alpha);
          const last = pts[pts.length - 1];
          this.label(item.name, last.x, last.y - 10, COLOR.friendly, alpha);
        }
        break;
      }
      case 'nai': {
        const c = pts[0];
        const r = this.metersToPx(item.radiusM ?? 400, item.pts[0].x, item.pts[0].y);
        dashedCircle(g, c.x, c.y, r, 8, 6);
        g.stroke({ width: 1.75, color: COLOR.isr, alpha });
        if (withLabels) this.label(item.name, c.x, c.y - r - 9, COLOR.isr, alpha);
        break;
      }
      case 'aa': {
        dashedPolyline(g, [...pts, pts[0]], 8, 6);
        g.stroke({ width: 1.75, color: COLOR.friendly, alpha });
        if (withLabels) {
          // Above the box, not in the middle of it: the assembly area is the one graphic that is full
          // of unit symbols, and a centred label sat on top of four callsigns at H-hour.
          let minX = Infinity;
          let maxX = -Infinity;
          let minY = Infinity;
          for (const p of pts) {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
          }
          this.label(item.name, (minX + maxX) / 2, minY - 10, COLOR.friendly, alpha);
        }
        break;
      }
    }
  }

  // ------------------------------------------------------------------ input

  private handlePointerDown = (e: PointerEvent): void => {
    this.downAt = { x: e.clientX, y: e.clientY, t: performance.now() };
  };

  private handleClick = (e: MouseEvent): void => {
    // A drag that ends on the map is a camera move, not a click.
    if (this.downAt && Math.hypot(e.clientX - this.downAt.x, e.clientY - this.downAt.y) > 4) return;
    const rect = this.container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    let best: { id: number; d: number } | null = null;
    for (const [id, node] of this.unitNodes) {
      const d = Math.hypot(node.screenX - px, node.screenY - py);
      if (d <= HIT_RADIUS_PX && (!best || d < best.d)) best = { id, d };
    }
    if (best && this.unitClick) {
      this.unitClick(best.id);
      return;
    }
    if (this.mapClick) {
      const ll = this.map.unproject([px, py]);
      this.mapClick(this.proj.lngLatToCm(ll.lng, ll.lat));
    }
  };
}

// --------------------------------------------------------------- primitives

function polyline(g: Graphics, pts: { x: number; y: number }[]): void {
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
}

function dashedPolyline(g: Graphics, pts: { x: number; y: number }[], dash: number, gap: number): void {
  let carry = 0;
  let drawing = true;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) continue;
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    let t = 0;
    while (t < len) {
      const seg = (drawing ? dash : gap) - carry;
      const step = Math.min(seg, len - t);
      if (drawing) {
        g.moveTo(a.x + ux * t, a.y + uy * t);
        g.lineTo(a.x + ux * (t + step), a.y + uy * (t + step));
      }
      t += step;
      if (step === seg) {
        drawing = !drawing;
        carry = 0;
      } else {
        carry += step;
      }
    }
  }
}

function dashedCircle(g: Graphics, cx: number, cy: number, r: number, dash: number, gap: number): void {
  const steps = Math.max(24, Math.min(160, Math.round(r * 1.2)));
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  dashedPolyline(g, pts, dash, gap);
}

function arrowHead(g: Graphics, from: { x: number; y: number }, to: { x: number; y: number }, size: number): void {
  const ang = Math.atan2(to.y - from.y, to.x - from.x);
  const spread = 0.42;
  g.poly([
    to.x,
    to.y,
    to.x - Math.cos(ang - spread) * size,
    to.y - Math.sin(ang - spread) * size,
    to.x - Math.cos(ang + spread) * size,
    to.y - Math.sin(ang + spread) * size,
  ], true);
}

function centroid(pts: { x: number; y: number }[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const p of pts) {
    x += p.x;
    y += p.y;
  }
  return { x: x / pts.length, y: y / pts.length };
}

/** Linear blend between two packed RGB colors, used for the suppression tint. */
function mixTint(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * k);
  const gg = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (gg << 8) | bl;
}
